import React, { useEffect, useState } from "react";
import { db } from "../../lib/firebase";
import {
  collection,
  query,
  where,
  doc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Card } from "../common/Card";
import { Button } from "../common/Button";
import { toast } from "react-hot-toast";
import { KitchenOrder } from "../../types/index";
import { NoticeManager } from "../admin/NoticeManager";

const getVariant = (customizations: any) => {
  if (!customizations) return null;
  if (Array.isArray(customizations)) {
    const found = customizations.find(
      (c: any) => c.type === "variant" || c.label === "Size"
    );
    return found ? found.value : null;
  }
  return customizations.variant || null;
};

/** Resolve delivery address from common order field shapes. */
export const resolveDeliveryAddress = (order: Record<string, any>): string => {
  const candidates = [
    order.deliveryAddress,
    order.shippingAddress,
    order.address,
    order.customerAddress,
    order.dropAddress,
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    if (typeof raw === "string" && raw.trim()) return raw.trim();
    if (typeof raw === "object") {
      const formatted =
        raw.formatted ||
        raw.full ||
        raw.fullAddress ||
        [
          raw.line1,
          raw.line2,
          raw.block,
          raw.floor,
          raw.flat,
          raw.houseNumber,
          raw.communityName,
          raw.area,
          raw.city,
        ]
          .filter(Boolean)
          .join(", ");
      if (formatted) return String(formatted);
    }
  }
  return "No address on order";
};

type Props = {
  kitchenId: string;
  showNoticeBoard?: boolean;
};

export const KitchenOrderManager: React.FC<Props> = ({
  kitchenId,
  showNoticeBoard = true,
}) => {
  const [orders, setOrders] = useState<KitchenOrder[]>([]);
  const functions = getFunctions();

  useEffect(() => {
    const q = query(
      collection(db, "orders"),
      where("kitchenId", "==", kitchenId),
      // awaiting_chef_confirmation: ordered after an item's cut-off; the chef answers Accept / Decline.
      // (Unpaid WhatsApp PayU orders, status awaiting_payment, stay hidden until paid.)
      // out_for_delivery is included so a chef's own self-delivery stays visible with a "Mark delivered" step.
      where("status", "in", ["awaiting_chef_confirmation", "pending", "placed", "preparing", "ready", "out_for_delivery"])
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetchedOrders = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as KitchenOrder))
          // Orders a delivery partner has picked up leave the kitchen list; self-deliveries stay.
          .filter((o: any) => o.status !== "out_for_delivery" || o.selfDelivery === true);
        fetchedOrders.sort((a, b) => {
          const ua = a.status === "awaiting_chef_confirmation" ? 1 : 0;
          const ub = b.status === "awaiting_chef_confirmation" ? 1 : 0;
          if (ua !== ub) return ub - ua; // cut-off requests first: they time out
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
        });
        setOrders(fetchedOrders);
      },
      (err) => {
        console.error("Kitchen orders listener", err);
        toast.error("Couldn't load orders");
      }
    );
    return () => unsubscribe();
  }, [kitchenId]);

  const handleAccept = async (orderId: string) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { status: "preparing" });
      toast.success("Order accepted");
    } catch {
      toast.error("Failed to accept");
    }
  };

  const [answering, setAnswering] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const handleCutoff = async (orderId: string, decision: "accept" | "decline") => {
    if (decision === "decline" && !window.confirm("Decline this order? It will be cancelled and the customer refunded.")) return;
    setAnswering(orderId);
    try {
      const fn = httpsCallable(functions, "respondChefCutoffRequest");
      const res: any = await fn({ orderId, decision });
      if (res.data?.already) toast(res.data.message || "Already answered", { icon: "ℹ️" });
      else toast.success(decision === "accept" ? "Accepted: the customer has been told" : "Declined: order cancelled, customer told");
    } catch (err: any) {
      toast.error(String(err?.message || "Failed").replace(/^Firebase:\s*/i, "").slice(0, 140));
    } finally {
      setAnswering(null);
    }
  };

  const handleReady = async (orderId: string) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { status: "ready" });
      toast.success("Marked ready");
    } catch {
      toast.error("Failed");
    }
  };

  const [selfBusy, setSelfBusy] = useState<string | null>(null);

  const handleSelfDeliver = async (orderId: string) => {
    if (
      !window.confirm(
        "Deliver this order yourself?\n\nIt will show as \"On the way\" to the customer (with you as the deliverer), any delivery partner run for it is dropped, and a wallet-paid delivery fee is refunded to the customer.\n\nTap \"Mark delivered\" once you hand it over."
      )
    )
      return;
    setSelfBusy(orderId);
    try {
      const res: any = await httpsCallable(functions, "markOrderSelfDelivered")({ kitchenOrderId: orderId });
      toast.success(res.data?.message || "You're delivering this order. Tap Mark delivered when done.", { duration: 6000 });
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message ? String(err.message).replace(/^Firebase:\s*/i, "").slice(0, 140) : "Self deliver failed");
    } finally {
      setSelfBusy(null);
    }
  };

  const handleMarkDelivered = async (orderId: string) => {
    if (!window.confirm("Mark this order delivered? The customer will be told it has arrived.")) return;
    setSelfBusy(orderId);
    try {
      const res: any = await httpsCallable(functions, "markSelfDeliveryDelivered")({ kitchenOrderId: orderId });
      toast.success(res.data?.message || "Marked delivered");
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message ? String(err.message).replace(/^Firebase:\s*/i, "").slice(0, 140) : "Couldn't mark delivered");
    } finally {
      setSelfBusy(null);
    }
  };

  const statusLabel = (status: string) => {
    if (status === "pending" || status === "placed") return "New";
    if (status === "awaiting_chef_confirmation") return "After cut-off: can you make it?";
    if (status === "out_for_delivery") return "You're delivering";
    if (status === "ready") return "Ready for pickup";
    return status;
  };

  const statusClass = (status: string) => {
    if (status === "awaiting_chef_confirmation") return "bg-red-100 text-red-800";
    if (status === "pending" || status === "placed")
      return "bg-yellow-100 text-yellow-800";
    if (status === "preparing") return "bg-blue-100 text-blue-800";
    if (status === "out_for_delivery") return "bg-orange-100 text-orange-800";
    return "bg-green-100 text-green-800";
  };

  return (
    <div className="space-y-4 mt-2">
      {showNoticeBoard && (
        <div className="bg-orange-50 p-3 sm:p-4 rounded-xl border border-orange-100">
          <h4 className="font-bold text-orange-800 mb-2 text-sm">Notice board</h4>
          <NoticeManager />
        </div>
      )}

      <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
        Active orders
        <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs sm:text-sm">
          {orders.length}
        </span>
      </h2>

      {orders.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <p className="text-gray-400 text-base">No active orders</p>
          <p className="text-sm text-gray-400 mt-1">
            New web and WhatsApp orders show up here
          </p>
        </div>
      ) : (
        orders.map((order) => (
          <Card
            key={order.id}
            className="border-l-4 border-orange-500 shadow-sm p-4"
          >
            <div className="flex justify-between items-start gap-3 mb-3 border-b border-gray-100 pb-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span
                    className={`text-xs font-bold uppercase px-2 py-1 rounded ${statusClass(
                      order.status
                    )}`}
                  >
                    {statusLabel(order.status)}
                  </span>
                  <span className="text-gray-400 text-xs">
                    #{order.id.slice(-4).toUpperCase()}
                  </span>
                </div>
                <h3 className="font-bold text-gray-800 text-base truncate">
                  {order.customerName || "Customer"}
                </h3>
                <p className="text-xs text-gray-500">{order.customerPhone}</p>
                <p className="text-xs text-gray-500 mt-1 break-words">
                  📍 {resolveDeliveryAddress(order as any)}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <span className="block font-mono font-bold text-lg text-gray-900">
                  ₹{order.totalAmount}
                </span>
                <span className="text-xs text-gray-400">
                  {order.createdAt?.seconds
                    ? new Date(
                        order.createdAt.seconds * 1000
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : ""}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg mb-4 space-y-2">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm gap-2">
                  <div className="min-w-0">
                    <span className="font-bold text-gray-700">
                      {item.quantity}x {item.name}
                    </span>
                    {getVariant(item.customizations) && (
                      <span className="text-xs text-gray-500 ml-2 bg-white px-1.5 rounded border border-gray-200">
                        {getVariant(item.customizations)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {order.status === "awaiting_chef_confirmation" && (() => {
              const cr: any = (order as any).cutoffRequest || {};
              const exp = cr.expiresAt?.seconds ? cr.expiresAt.seconds * 1000 : 0;
              const minsLeft = exp ? Math.max(0, Math.ceil((exp - nowMs) / 60000)) : null;
              return (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 text-sm text-red-800">
                  <p className="font-semibold">Ordered after the cut-off</p>
                  <p className="mt-1">
                    {(cr.items || []).map((i: any) => `${i.name} (cut-off ${i.cutoff})`).join(", ")}
                  </p>
                  <p className="mt-1 text-xs">
                    {minsLeft === null ? "" : minsLeft > 0 ? `Please answer within ${minsLeft} min; ` : "Time is up; "}
                    with no answer the order is cancelled and the customer refunded automatically.
                  </p>
                </div>
              );
            })()}

            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              {order.status === "awaiting_chef_confirmation" && (
                <>
                  <Button
                    onClick={() => handleCutoff(order.id, "accept")}
                    disabled={answering === order.id}
                    className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto min-h-[44px]"
                  >
                    Accept (I can make it)
                  </Button>
                  <Button
                    onClick={() => handleCutoff(order.id, "decline")}
                    disabled={answering === order.id}
                    variant="secondary"
                    className="border-red-200 text-red-700 w-full sm:w-auto min-h-[44px]"
                  >
                    Decline
                  </Button>
                </>
              )}
              {(order.status === "placed" || order.status === "pending") && (
                <Button
                  onClick={() => handleAccept(order.id)}
                  className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto min-h-[44px]"
                >
                  Accept
                </Button>
              )}
              {order.status === "preparing" && (
                <Button
                  onClick={() => handleReady(order.id)}
                  className="bg-green-600 hover:bg-green-700 w-full sm:w-auto min-h-[44px]"
                >
                  Mark ready
                </Button>
              )}
              {order.status === "ready" && (
                <Button
                  onClick={() => handleSelfDeliver(order.id)}
                  disabled={selfBusy === order.id}
                  variant="secondary"
                  className="border-orange-200 text-orange-700 w-full sm:w-auto min-h-[44px]"
                >
                  {selfBusy === order.id ? "Starting…" : "Self deliver"}
                </Button>
              )}
              {order.status === "out_for_delivery" && (order as any).selfDelivery && (
                <div className="w-full flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
                  <p className="text-xs text-orange-800 bg-orange-50 rounded p-2">
                    You&apos;re delivering this. The customer sees &quot;On the way&quot;.
                  </p>
                  <Button
                    onClick={() => handleMarkDelivered(order.id)}
                    disabled={selfBusy === order.id}
                    className="bg-green-600 hover:bg-green-700 w-full sm:w-auto min-h-[44px]"
                  >
                    {selfBusy === order.id ? "Saving…" : "Mark delivered"}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

export default KitchenOrderManager;
